import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, shareReplay, takeUntil } from 'rxjs';
import { ICommand, IField } from '../../../../model/docking';
import { IMaintenanceContext } from '../../../../model/maintenance';
import { EnvService } from '../../configuration/env.service';
import { LocationContextService } from '../../location-context/location-context.service';
import { MaintenanceService } from '../maintenance.service';
import { ILocationContext } from '../../../../model/location-context';
import { AuthenticationService } from '../../authentication/authentication.service';
import { MaintenanceTestCommandInputComponent } from '../maintenance-test-command-input/maintenance-test-command-input.component';
import { FopModeService } from 'client/app/fop-mode/fop-mode.service';
import { FopLoginMode } from 'client/app/fop-mode/fop-mode.model';
import { CommandPinCodeComponent } from '../../docking/command-pin-code/command-pin-code.component';
import { BasicComponent } from '../../basic-component';

@Component({
    selector: 'app-maintenance-test-commands',
    templateUrl: './maintenance-test-commands.component.html',
    styleUrls: ['./maintenance-test-commands.component.scss']
})
export class MaintenanceTestCommandsComponent extends BasicComponent implements OnInit {
    deviceId: string;
    operator: string;
    locationContext: ILocationContext;
    maintenanceContext: IMaintenanceContext;
    skipPin: boolean;

    @ViewChildren(MaintenanceTestCommandInputComponent) inputs: QueryList<MaintenanceTestCommandInputComponent>;

    constructor(
        private readonly maintenanceService: MaintenanceService,
        private readonly locationContextService: LocationContextService,
        private readonly authenticationService: AuthenticationService,
        private readonly env: EnvService,
        private readonly fopModeService: FopModeService,
        public readonly dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        this.deviceId = this.maintenanceService.getDeviceIdFromSession();
        if (!this.deviceId) {
            throw new Error('Device id is undefined');
        }
        this.operator = this.authenticationService.getUsername();
        if (!this.operator) {
            throw new Error('Operator is undefined');
        }
        this.locationContext = this.locationContextService.getLocationContext();
        if (!this.locationContext) {
            throw new Error('Location context is undefined');
        }
        this.maintenanceService.requestMaintenanceContext(this.locationContext, this.deviceId);

        this.maintenanceService
            .getMaintenanceContext()
            .pipe(takeUntil(this.componentDestroyed$), debounceTime(500), shareReplay())
            .subscribe(maintenanceContext => (this.maintenanceContext = maintenanceContext));

        if (this.env.config.skipPin) {
            this.fopModeService
                .getFopConfig()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(fopConfig => {
                    this.skipPin = fopConfig.loginMode === FopLoginMode.UserLogin;
                });
        }
    }

    triggerTestCommand(command: ICommand) {
        const localCommand = { ...command };

        const fields: IField[] = [];
        if (Array.isArray(this.inputs)) {
            this.inputs.forEach(input => {
                fields.push({
                    key: input.fieldDefItem.key,
                    value: input.getIpAddress()
                });
            });
        }
        localCommand.fields = fields;

        if (command.PIN) {
            if (this.skipPin) {
                const pinField = { key: 'pin', value: 'MOP' };
                localCommand.fields.push(pinField);
                this.maintenanceService.triggerTestCommand(
                    this.locationContextService.getLocationContext(),
                    localCommand,
                    this.deviceId,
                    this.operator
                );
            } else {
                const dialogRef = this.dialog.open(CommandPinCodeComponent, {
                    disableClose: false,
                    data: {
                        userPinStorageService: this.maintenanceService
                    }
                });
                dialogRef.afterClosed().subscribe(() => {
                    const pinField = { key: 'pin', value: this.maintenanceService.userEnteredPin };
                    localCommand.fields.push(pinField);
                    this.maintenanceService.triggerTestCommand(
                        this.locationContextService.getLocationContext(),
                        localCommand,
                        this.deviceId,
                        this.operator
                    );
                });
            }
        } else {
            this.maintenanceService.triggerTestCommand(
                this.locationContextService.getLocationContext(),
                localCommand,
                this.deviceId,
                this.operator
            );
        }
    }

    triggerTestBackCommand() {
        this.maintenanceService.triggerTestBackCommand(this.locationContextService.getLocationContext(), this.deviceId);
    }
}
